<!-- KnotCarousel.vue -->
<template>
  <div id="container">
    <template v-if="isMultiPage">
      <div id="content" @scroll="handleScroll" ref="container">
        <div
          v-for="(slotName, index) in slotNames"
          :key="index"
          class="slide"
          :class="getSlideClass(slotName)"
        >
          <slot :name="slotName"></slot>
        </div>
      </div>
      <div id="navigation">
        <svg
          v-if="!useSlideNames"
          @click="setPage(page - 1)"
          viewBox="0 0 54 54"
          version="1.1"
          xmlns="http://www.w3.org/2000/svg"
          xmlns:xlink="http://www.w3.org/1999/xlink"
          xml:space="preserve"
          xmlns:serif="http://www.serif.com/"
          class="nav-buttons"
          :class="{ disabled: isFirstPage }"
        >
          <circle cx="27" cy="27" r="25.1" />
          <path d="M32.25,15.75l-15,11.25l15,11.25" />
        </svg>
        <div
          class="nav-text-buttons"
          v-else-if="!isFirstPage"
          @click="setPage(page - 1)"
        >
          {{ slotNames[page - 1] }}
        </div>
        <div class="nav-text-buttons-fill" v-else></div>
        <transition-group name="visual-cue" tag="div" id="visual-cue">
          <img
            v-for="index in pages"
            :key="index"
            :src="visualCueImage(index)"
            @click="setPage(index - 1)"
            :alt="visualCueImageAlt(index)"
            :title="'Jump to Page ' + index"
          />
        </transition-group>
        <svg
          v-if="!useSlideNames"
          viewBox="0 0 54 54"
          version="1.1"
          xmlns="http://www.w3.org/2000/svg"
          xmlns:xlink="http://www.w3.org/1999/xlink"
          xml:space="preserve"
          xmlns:serif="http://www.serif.com/"
          @click="setPage(page + 1)"
          class="nav-buttons"
          :class="{ disabled: isLastPage }"
        >
          <circle cx="27" cy="27" r="25.1" />
          <path d="M21.75,38.25l15,-11.25l-15,-11.25" />
        </svg>
        <div
          class="nav-text-buttons"
          v-else-if="!isLastPage"
          @click="setPage(page + 1)"
        >
          {{ slotNames[page + 1] }}
        </div>
        <div class="nav-text-buttons-fill" v-else></div>
      </div>
    </template>
    <template v-else-if="isSinglePage">
      <slot :name="slotNames[0]"></slot>
    </template>
  </div>
</template>

<script>
const viewport = window.visualViewport;
export default {
  name: "KnotCarousel",
  emits: ["set-puzzle", "page-changed"],
  props: {
    slideClass: {
      type: Object,
      required: false,
    },
    selectedSlide: {
      type: String,
    },
    useSlideNames: {
      type: Boolean,
      default: false,
    },
    exclude: {
      type: Array,
      default: function () {
        return [];
      },
    },
  },
  data() {
    return {
      gap: 10,
      debounceTimeout: null,
      lastScroll: 0,
      page: 0,
      pageWidth: 0,
    };
  },
  mounted() {
    window.addEventListener("resize", this.setPageWidth);
    viewport.addEventListener("resize", this.setPageWidth);
    this.setPageWidth();
    if (this.selectedSlide !== null) {
      this.setPageByName(this.selectedSlide);
    }
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.setPageWidth);
    viewport.removeEventListener("resize", this.setPageWidth);
  },
  computed: {
    slotNames() {
      return Object.keys(this.$slots).filter(
        (key) => !this.exclude.includes(key)
      );
    },
    pages() {
      return this.slotNames.length;
    },

    isFirstPage() {
      return this.page === 0;
    },
    isLastPage() {
      return this.page >= this.pages - 1;
    },
    isMultiPage() {
      return this.pages > 1;
    },
    isSinglePage() {
      return this.pages === 1;
    },
  },
  watch: {
    pageWidth(value) {
      if (this.$refs.container !== undefined) {
        document.documentElement.style.setProperty(
          "--slide-width",
          `${value}px`
        );
      }
    },
  },
  methods: {
    getSlideClass(slide) {
      if (this.slideClass === undefined) {
        return;
      }

      if (slide in this.slideClass) {
        return this.slideClass[slide];
      }
      if ("All" in this.slideClass) {
        return this.slideClass.All;
      }
    },
    setPageWidth() {
      if (this.isMultiPage) {
        this.pageWidth = this.$refs.container.clientWidth;
      }
    },
    visualCueImage(page) {
      if (page - 1 === this.page) {
        return "images/circlefilled.svg";
      }
      return "images/circle.svg";
    },
    visualCueImageAlt(page) {
      if (page - 1 === this.page) {
        return "Filled Circle";
      }
      return "Bordered Circle";
    },
    setPage(newPage, behavior = "smooth") {
      if (newPage < 0) {
        newPage = 0;
      }
      if (newPage > this.pages - 1) {
        newPage = this.pages - 1;
      }
      this.$emit(
        "page-changed",
        newPage,
        this.page,
        behavior,
        this.slotNames[newPage]
      );
      this.page = newPage;
      if (this.$refs.container !== undefined && this.$refs.container !== null) {
        this.$refs.container.scrollTo({
          left: newPage * (this.pageWidth + this.gap),
          behavior: behavior,
        });
      }
    },
    setPageByName(name) {
      this.setPage(Math.max(0, this.slotNames.indexOf(name)), "instant");
    },
    handleScroll(event) {
      const currentScroll = Math.round(event.target.scrollLeft);
      if (currentScroll == this.lastScroll) {
        return;
      }
      const isScrollingRight = currentScroll > this.lastScroll;

      this.lastScroll = currentScroll;

      clearTimeout(this.debounceTimeout);
      this.debounceTimeout = setTimeout(() => {
        let newPage;
        if (isScrollingRight) {
          newPage = Math.ceil(Math.round(this.lastScroll / this.pageWidth, 2));
        } else {
          newPage = Math.floor(Math.round(this.lastScroll / this.pageWidth, 2));
        }
        this.setPage(newPage);
      }, 100);
    },
  },
};
</script>

<style scoped>
#container {
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  gap: 1.5vh;
  justify-content: center;
}
#content {
  border-bottom: 1px solid var(--border-color);
  display: flex;
  flex-direction: row;
  width: var(--canvas-width);
  overflow-x: scroll;
  gap: 10px;
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
  align-items: center;
  flex: 1 0 var(--slide-width);
  scroll-behavior: auto;
  -webkit-overflow-scrolling: auto;
}

/* #content::-webkit-scrollbar {
  display: none;
} */
h2 {
  text-align: center;
}
.slide {
  width: 100%;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 1vh;
  flex: 0 0 auto;
  padding: 5px;
  overflow-y: auto;
  overflow-x: hidden;
  height: 100%;
  justify-content: safe center;
}
#navigation {
  display: flex;
  justify-content: space-between;
  padding: 0 10px;
  align-items: center;
}
#navigation .nav-buttons {
  aspect-ratio: 1/1;
  height: 4.5vh;
  fill-rule: evenodd;
  clip-rule: evenodd;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-miterlimit: 1.5;
  fill: none;
  stroke: var(--border-color);
  stroke-width: 3.8px;
  cursor: pointer;
}

#navigation .nav-text-buttons-fill,
#navigation .nav-text-buttons {
  height: 4.5vh;
  aspect-ratio: 2.75/1;
  display: flex;
  justify-content: center;
  align-items: center;
  color: var(--border-color);
  font-weight: bold;
  font-size: 0.7em;
  text-transform: uppercase;
}
#navigation .nav-text-buttons {
  border: 0.4vh solid var(--border-color);
  border-radius: 0.4vh;

  cursor: pointer;
}

#navigation .nav-buttons.disabled {
  stroke: var(--disabled);
  cursor: auto;
}
#visual-cue {
  display: flex;
  gap: 1.5vmin;
}

#visual-cue img {
  aspect-ratio: 1/1;
  height: 2.5vmin;
  cursor: pointer;
  max-height: 1.5vh;
}

/* .visual-cue-enter-active,
.visual-cue-leave-active {
  transition: all 1s ease;
}
.visual-cue-move {
  transition: all 0.75s ease;
}

.visual-cue-enter-from {
  opacity: 0;
}

.visual-cue-leave-to {
  opacity: 0;
} */
.at-top {
  justify-content: start;
}
.no-scroll {
  overflow: hidden;
}
</style>
